import React, { useEffect, useState } from 'react';
import { X, Trophy, Star, ArrowRight, Home, Sparkles } from 'lucide-react';

const TaskSuccessModal = ({
    isOpen,
    onClose,
    task,
    score,
    passingScore,
    nextTask,
    onViewNextTask,
    onBackToDashboard
}) => {
    const [showConfetti, setShowConfetti] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setShowConfetti(true);
            // Stop confetti after 3 seconds
            const timer = setTimeout(() => setShowConfetti(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const isPassed = score === null || score >= (passingScore || 70);

    return (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
            {/* Confetti Animation */}
            {showConfetti && (
                <div className="fixed inset-0 pointer-events-none overflow-hidden z-[201]">
                    {[...Array(50)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute animate-confetti"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: '-10px',
                                animationDelay: `${Math.random() * 2}s`,
                                animationDuration: `${2 + Math.random() * 2}s`,
                            }}
                        >
                            <div
                                className="w-3 h-3 rounded-sm"
                                style={{
                                    backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'][Math.floor(Math.random() * 7)],
                                    transform: `rotate(${Math.random() * 360}deg)`,
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 relative z-[202]">
                {/* Header with Celebration */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white text-center relative overflow-hidden">
                    {/* Sparkle decorations */}
                    <Sparkles className="absolute top-4 left-4 w-6 h-6 text-yellow-300 animate-pulse" />
                    <Sparkles className="absolute top-6 right-8 w-4 h-4 text-yellow-300 animate-pulse" style={{ animationDelay: '0.5s' }} />
                    <Sparkles className="absolute bottom-4 right-4 w-5 h-5 text-yellow-300 animate-pulse" style={{ animationDelay: '1s' }} />

                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                        <Trophy className="w-10 h-10 text-yellow-300" />
                    </div>

                    <h2 className="text-2xl font-bold mb-2">Task Completed!</h2>
                    <p className="text-white/90">Great job on finishing "{task?.title}"</p>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Score Display (if applicable) */}
                    {score !== null && score !== undefined && (
                        <div className={`rounded-xl p-6 text-center ${isPassed ? 'bg-green-50' : 'bg-red-50'}`}>
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <Star className={`w-6 h-6 ${isPassed ? 'text-green-500' : 'text-red-500'}`} />
                                <span className={`text-lg font-semibold ${isPassed ? 'text-green-700' : 'text-red-700'}`}>
                                    Your Score
                                </span>
                            </div>
                            <p className={`text-5xl font-bold ${isPassed ? 'text-green-600' : 'text-red-600'}`}>
                                {score}%
                            </p>
                            {passingScore && (
                                <p className="text-sm text-gray-500 mt-2">
                                    Passing score: {passingScore}%
                                </p>
                            )}
                        </div>
                    )}

                    {/* Next Task Preview */}
                    {nextTask && (
                        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                            <p className="text-xs text-primary font-semibold uppercase tracking-wide mb-2">Up Next</p>
                            <p className="font-semibold text-gray-800">{nextTask.title}</p>
                            <p className="text-sm text-gray-500 capitalize">{nextTask.task_type} • {nextTask.estimated_duration || '?'}m</p>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="px-6 pb-6 space-y-3">
                    {nextTask && (
                        <button
                            onClick={onViewNextTask}
                            className="w-full py-3 px-4 bg-primary hover:bg-primary-hover text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                        >
                            View Next Task
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    )}
                    <button
                        onClick={onBackToDashboard}
                        className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                        <Home className="w-5 h-5" />
                        Back to Dashboard
                    </button>
                </div>
            </div>

            {/* Confetti CSS Animation */}
            <style>{`
                @keyframes confetti-fall {
                    0% {
                        transform: translateY(0) rotate(0deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(100vh) rotate(720deg);
                        opacity: 0;
                    }
                }
                .animate-confetti {
                    animation: confetti-fall linear forwards;
                }
            `}</style>
        </div>
    );
};

export default TaskSuccessModal;
